import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, DoCheck, IterableDiffers, IterableDiffer, ViewEncapsulation, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/app.materials';
import { MatAccordion } from '@angular/material/expansion';
import { FilterOption } from '../../../components/filter-field/filter-field.component';

interface FilterConfig {
  key: string;
  label: string;
  totalCountKey: string;
  optionsKey: string;
}

interface AssetClassConfig {
  name: string;
  key: string;
  filters: FilterConfig[];
}

@Component({
    selector: 'app-sidebar-filter',
    templateUrl: './sidebar-filter.component.html',
    styleUrls: ['./sidebar-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, FormsModule, MaterialModule]
})
export class SidebarFilterComponent implements OnChanges, DoCheck {

  @Input() currentFilters: any;
  @Input() filterLists: any;

  @Output() onFilterEvent = new EventEmitter<any>();
  @Output() onResetFilterEvent = new EventEmitter<any>();

  hasFilters = false;

  @ViewChild(MatAccordion) accordion?: MatAccordion;

  // Configuration-driven approach
  assetClassConfigs: AssetClassConfig[] = [
    {
      name: 'Equity',
      key: 'Equity',
      filters: [
        { key: 'broad_geo_desc', label: 'Region', totalCountKey: 'broad_geo_desc', optionsKey: 'broad_geo_desc' },
        { key: 'specific_geo_desc', label: 'Country', totalCountKey: 'specific_geo_desc', optionsKey: 'specific_geo_desc' },
        { key: 'sector', label: 'Sector', totalCountKey: 'sector', optionsKey: 'sector' },
        { key: 'focus_desc', label: 'Strategy', totalCountKey: 'focus_desc', optionsKey: 'focus_desc' },
        { key: 'EconDevelopmentDescription', label: 'Economic Development', totalCountKey: 'EconDevelopmentDescription', optionsKey: 'EconDevelopmentDescription' },
        { key: 'niche_desc', label: 'Niche', totalCountKey: 'niche_desc', optionsKey: 'niche_desc' }
      ]
    },
    {
      name: 'Fixed Income',
      key: 'Fixed Income',
      filters: [
        { key: 'broad_geo_desc', label: 'Region', totalCountKey: 'broad_geo_desc', optionsKey: 'broad_geo_desc' },
        { key: 'specific_geo_desc', label: 'Country', totalCountKey: 'specific_geo_desc', optionsKey: 'specific_geo_desc' },
        { key: 'category_desc', label: 'Bond Type', totalCountKey: 'category_desc', optionsKey: 'category_desc' },
        { key: 'EconDevelopmentDescription', label: 'Economic Development', totalCountKey: 'EconDevelopmentDescription', optionsKey: 'EconDevelopmentDescription' },
        { key: 'niche_desc', label: 'Niche', totalCountKey: 'niche_desc', optionsKey: 'niche_desc' },
        { key: 'focus_desc', label: 'Focus', totalCountKey: 'focus_desc', optionsKey: 'focus_desc' }
      ]
    },
    {
      name: 'Commodities',
      key: 'Commodities',
      filters: [
        { key: 'category_desc', label: 'Segment', totalCountKey: 'category_desc', optionsKey: 'category_desc' },
        { key: 'focus_desc', label: 'Commodity', totalCountKey: 'focus_desc', optionsKey: 'focus_desc' }
      ]
    },
    {
      name: 'Currency',
      key: 'Currency',
      filters: [
        { key: 'focus_currency_long', label: 'Strategy - long', totalCountKey: 'focus_currency_long', optionsKey: 'focus_currency_long' },
        { key: 'focus_currency_short', label: 'Strategy - short', totalCountKey: 'focus_currency_short', optionsKey: 'focus_currency_short' }
      ]
    },
    /* {
      name: 'Alternatives',
      key: 'Alternatives',
      filters: [
        { key: 'segment_desc', label: 'Segment', totalCountKey: 'segment_desc', optionsKey: 'segment_desc' },
        { key: 'focus_desc', label: 'Strategy', totalCountKey: 'focus_desc', optionsKey: 'focus_desc' },
        { key: 'niche_desc', label: 'Niche', totalCountKey: 'niche_desc', optionsKey: 'niche_desc' }
      ]
    } */
  ];

  // Precomputed grouped options for Fixed Income -> category_desc
  categoryDescGroups: { groupName: string; options: FilterOption[] }[] = [];

  // Differ to detect in-place mutations to the category_desc array
  private categoryDescDiffer: IterableDiffer<FilterOption> | null = null;

  constructor(private iterableDiffers: IterableDiffers) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentFilters']) {
      this.currentFilters = changes['currentFilters'].currentValue;
    }
    if (changes['filterLists']) {
      this.filterLists = changes['filterLists'].currentValue;
      // Reset differ when reference changes so we reinitialize tracking
      this.categoryDescDiffer = null;
    }
  }

  onFilter() {
    this.onFilterEvent.emit(this.currentFilters);
  }

  panelChangedTo(panel: string) {
    this.categoryDescGroups = [];
    this.onResetFilterEvent.emit();
    setTimeout(() => {
      this.currentFilters.filters.asset_class = panel;
      this.onFilter();
      if (panel === 'Fixed Income') {
        const options = this.getFilterOptions('category_desc');
        this.ensureDifferInitialized(options);
        this.categoryDescGroups = this.interfaceGroupedOptions(options);
      } else {
        this.categoryDescGroups = [];
      }
    }, 100);
  }



  getTotalCount(assetClassKey: string): number {
    return this.filterLists?.total_counts?.[assetClassKey] || 0;
  }

  getFilterOptions(optionsKey: string): FilterOption[] {
    return this.filterLists?.[optionsKey] || [];
  }

  getFilterTotalCount(totalCountKey: string): number {
    return this.filterLists?.total_counts?.[totalCountKey] || 0;
  }

  // Grouping helpers for Fixed Income -> category_desc
  interfaceGroupedOptions(options: FilterOption[]): { groupName: string; options: FilterOption[] }[] {
    const groupsMap = new Map<string, FilterOption[]>();
    for (const option of options || []) {
      const groupName = this.getFirstPart(option.name);
      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, []);
      }
      groupsMap.get(groupName)!.push(option);
    }
    return Array.from(groupsMap.entries()).map(([groupName, opts]) => ({ groupName, options: opts }));
  }

  getSecondPart(value: string): string {
    if (!value) { return ''; }
    const parts = value.split(',');
    return parts.length > 1 ? parts[1].trim() : value;
  }

  private getFirstPart(value: string): string {
    if (!value) { return 'Other'; }
    const idx = value.indexOf(',');
    return idx >= 0 ? value.substring(0, idx).trim() : 'Other';
  }

  // Initialize differ for custom change detection
  private ensureDifferInitialized(list: FilterOption[] | null | undefined): void {
    if (!this.categoryDescDiffer) {
      this.categoryDescDiffer = this.iterableDiffers.find(list || []).create<FilterOption>();
    }
  }

  private recomputeCategoryDescGroups(): void {
    const options = this.getFilterOptions('category_desc');
    this.categoryDescGroups = this.interfaceGroupedOptions(options);
  }

  ngDoCheck(): void {
    this.hasFilters = Object.keys(this.currentFilters?.filters || {}).length > 0;
    // Only track updates when Fixed Income panel is active
    if (this.currentFilters?.filters?.asset_class !== 'Fixed Income') {
      return;
    }
    const options = this.getFilterOptions('category_desc');
    this.ensureDifferInitialized(options);
    const diff = this.categoryDescDiffer!.diff(options);
    if (diff) {
      this.recomputeCategoryDescGroups();
    }
  }

  // UI helpers for highlighting active filters
  isFilterActive(filterKey: string): boolean {
    const value = this.currentFilters?.filters?.[filterKey];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  }

  isAssetClassActive(assetClass: AssetClassConfig): boolean {
    return (assetClass?.filters || []).some(f => this.isFilterActive(f.key));
  }

  onResetFilter() {
    this.currentFilters.filters = {};
    // close expansion panels
    this.accordion?.closeAll();
    this.categoryDescGroups = [];
    this.categoryDescDiffer = null;
    this.onFilter();
  }
}
